import { useState, ChangeEvent, useEffect, useContext } from "react";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogActions } from "@remo-co/ui-core/src/components/DialogActions";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogTitle } from "@remo-co/ui-core/src/components/DialogTitle";
import { IconButtonWithTooltip } from "@remo-co/ui-core/src/components/IconButtonWithTooltip";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { useI18n } from "i18n";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useFloorNames } from "modules/floors/hooks/useFloorNames";
import floorApis from "modules/floors/api";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { styles } from "./styles";

interface Props {
  eventId: string;
  theaterId: string | null;
  spaces: string[];
  close: () => void;
}

export const EditFloorNamesForm = ({
  eventId,
  theaterId,
  spaces,
  close,
}: Props) => {
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const { track } = useContext(TRACKING_CONTEXT);
  const floorNames = useFloorNames(theaterId);
  const [pendingFloorNames, setPendingFloorNames] = useState(floorNames);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useI18n(["settings", "common"]);

  useEffect(() => {
    setPendingFloorNames(floorNames);
  }, [floorNames]);

  const handleNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    floorId: string,
  ) => {
    setPendingFloorNames((prev) => ({
      ...prev,
      [floorId]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (theaterId) {
      setIsLoading(true);
      const res = await floorApis.renameEventFloors(
        eventId,
        theaterId,
        pendingFloorNames,
      );

      if (!res.isSuccess) {
        setIsLoading(false);
        addErrorNotification({
          message: t("settings:name.event.floors.error"),
        });

        return;
      }
      track(Events.FLOOR_RENAMING_SUCCESS, {
        eventId,
      });
      setIsLoading(false);
      addSuccessNotification({
        message: t("settings:name.event.floors.success"),
        position: "tr",
      });
      close();
    }
  };

  const handleCancelClose = () => {
    track(Events.FLOOR_RENAMING_CANCELLED, {
      eventId,
    });
    close();
  };

  useEffect(() => {
    track(Events.FLOOR_RENAMING_STARTED, {
      eventId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      data-testid="name-event-floors-dialog"
      onClose={close}
      aria-labelledby={t("settings:name.event.floors")}
      fullWidth
      open
      sx={{
        paper: styles.root,
      }}
    >
      <DialogTitle sx={styles.header}>
        <Typography variant="h3">{t("settings:name.event.floors")}</Typography>
        <IconButtonWithTooltip
          id="close-name-event-floors-dialog"
          title={t("common:button.close")}
          data-testid="close-name-event-floors-dialog"
          onClick={handleCancelClose}
          size="small"
          sx={styles.closeButton}
        >
          <Close />
        </IconButtonWithTooltip>
      </DialogTitle>
      <DialogContent sx={styles.inputContainer}>
        {spaces.map((floorId, index) => (
          <Input
            size="sm"
            fullWidth
            getRemainingCharsMessage={(key) =>
              t("common:character.remaining", { key })
            }
            label={t("event:top.bar.floor", { floor: index + 1 })}
            name="name"
            placeholder={t("event:top.bar.floor", { floor: index + 1 })}
            value={
              pendingFloorNames[floorId] ??
              t("event:top.bar.floor", { floor: index + 1 })
            }
            onChange={(event) => handleNameChange(event, floorId)}
            inputProps={{ maxLength: 25, "data-testid": "floor-name" }}
          />
        ))}
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button
          variant="secondary"
          color="blue"
          loading={isLoading}
          onClick={handleSubmit}
        >
          {t("common:button.save")}
        </Button>
        <Button
          variant="tertiary"
          color="blue"
          disabled={isLoading}
          onClick={handleCancelClose}
        >
          {t("common:button.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
